/**
 * Public tracking (FR-CLP-02), no sign-in: /track/<company code>. Shows the status in plain words
 * and the hub city only — never personal data. Works only when the company switched public
 * tracking on (Admin › Settings); otherwise every number answers "not found". Rate-limited.
 */
import { PackageSearch } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { BRAND } from '../../../config/brand';
import { CEP } from '../../../lib/control';

type Answer = { found: false; message: string } | { found: true; status: string; hubCity: string | null; updatedAt: string | null };

export default function PublicTrackPage() {
  const tenant = decodeURIComponent(window.location.pathname.split('/')[2] ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !tenant) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/v1${CEP.track(tenant, code.trim())}`);
      if (res.status === 429) { setError('Too many look-ups. Wait a minute and try again.'); return; }
      if (!res.ok) { setError('Tracking is not available right now.'); return; }
      setAnswer((await res.json()) as Answer);
    } catch {
      setError('No connection. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 px-4 py-8 text-ink">
      <h1 className="display flex items-center gap-2 text-2xl"><PackageSearch aria-hidden className="h-7 w-7 text-brand" />Track a parcel</h1>
      {!tenant ? <p className="text-sm text-muted">Use the tracking link your sender gave you.</p> : (
        <form onSubmit={(e) => void submit(e)} className="grid gap-3">
          <label htmlFor="trk" className="text-sm font-semibold">Tracking number</label>
          <input id="trk" value={code} onChange={(e) => setCode(e.target.value)} autoFocus autoComplete="off" className="min-h-touch rounded-[4px] border border-strong bg-card px-3 text-lg" placeholder="Waybill or reference" />
          <button type="submit" disabled={busy || !code.trim()} className="min-h-touch rounded-tag bg-brand px-4 font-semibold text-brand-ink disabled:opacity-50">{busy ? 'Looking…' : 'Track'}</button>
        </form>
      )}
      {error && <p role="alert" className="text-sm font-semibold text-danger">{error}</p>}
      {answer && (
        <section aria-live="polite" className="rounded-panel border border-line bg-card p-4">
          {answer.found ? (
            <>
              <p className="code text-sm text-muted">{code.trim()}</p>
              <p className="display text-3xl">{answer.status}</p>
              {answer.hubCity && <p className="text-sm text-muted">Hub: {answer.hubCity}</p>}
              {answer.updatedAt && <p className="text-xs text-muted">Last update {new Date(answer.updatedAt).toLocaleString('en-PH', { timeZone: 'Asia/Manila' })}</p>}
            </>
          ) : <p>{answer.message}</p>}
        </section>
      )}
      <p className="mt-6 text-xs text-muted">{BRAND.product}. For delivery questions, contact your sender.</p>
    </main>
  );
}
